import {
  type JSX,
  type Ref,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { Button } from "@frontend/components/atoms/Button";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";

import { Menu } from "./Menu";
import {
  type MenuDataProps,
  type MenuItemOption,
  menuDataSchema,
} from "./Menu.schema";
import { MenuContent } from "./MenuContent";
import { MenuItem } from "./MenuItem";
import { MenuTrigger } from "./MenuTrigger";
import {
  MENU_CONTENT_TAG_NAME,
  MENU_ITEM_TAG_NAME,
  MENU_TAG_NAME,
  MENU_TRIGGER_TAG_NAME,
} from "./constants";

export interface MenuHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [MENU_TAG_NAME]: { props: MenuDataProps; handle: MenuHandle };
  }
  interface InertTagNames {
    [MENU_TRIGGER_TAG_NAME]: true;
    [MENU_CONTENT_TAG_NAME]: true;
    [MENU_ITEM_TAG_NAME]: true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-menu": HTMLElement & MenuHandle;
  }
}

const RWCMenuTrigger = rwc(MENU_TRIGGER_TAG_NAME);

const RWCMenuContent = rwc(MENU_CONTENT_TAG_NAME);

const RWCMenuItem = rwc(MENU_ITEM_TAG_NAME);

export function readMenuSlotted(el: HTMLElement): Partial<MenuDataProps> {
  const triggerEl = el.querySelector<HTMLElement>(
    `:scope > ${RWCMenuTrigger.tagName}`,
  );

  const items: MenuItemOption[] = Array.from(
    el.querySelectorAll<HTMLElement>(
      `:scope > ${RWCMenuContent.tagName} > ${RWCMenuItem.tagName}`,
    ),
  ).map((item) => ({
    value: item.getAttribute("value") ?? "",
    label: item.getAttribute("label") ?? item.textContent?.trim() ?? "",
    icon: item.getAttribute("icon") ?? undefined,
    disabled: item.hasAttribute("disabled"),
  }));

  return {
    items,
    triggerLabel:
      triggerEl?.getAttribute("label") ??
      triggerEl?.textContent?.trim() ??
      undefined,
    triggerIcon: triggerEl?.getAttribute("icon") ?? undefined,
    triggerVariant:
      (triggerEl?.getAttribute("variant") as MenuDataProps["triggerVariant"]) ??
      undefined,
    triggerSize:
      (triggerEl?.getAttribute("size") as MenuDataProps["triggerSize"]) ??
      undefined,
    onOpenChange: (open) => {
      if (open) {
        el.setAttribute("open", "");
      } else {
        el.removeAttribute("open");
      }

      el.dispatchEvent(
        new CustomEvent("menu:change", { detail: { open }, bubbles: true }),
      );
    },
    onSelect: (value, item) => {
      el.dispatchEvent(
        new CustomEvent("menu:select", {
          detail: { value, item },
          bubbles: true,
        }),
      );
    },
  };
}

function RWCMenuComponent({
  ref,
  items = [],
  defaultOpen,
  open: controlledOpen,
  size,
  align,
  side,
  arrow,
  triggerLabel,
  triggerIcon,
  triggerVariant,
  triggerSize,
  onOpenChange,
  onSelect,
}: Partial<MenuDataProps> & { ref?: Ref<MenuHandle> }): JSX.Element {
  const [open, setOpen] = useState(controlledOpen ?? defaultOpen ?? false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: () => handleOpenChange(true),
      close: () => handleOpenChange(false),
      toggle: () =>
        setOpen((prev) => {
          const next = !prev;

          onOpenChange?.(next);

          return next;
        }),
    }),
    [handleOpenChange, onOpenChange],
  );

  useLayoutEffect(() => {
    if (controlledOpen !== undefined) {
      setOpen(controlledOpen);
    }
  }, [controlledOpen]);

  return (
    <Menu
      open={open}
      onOpenChange={handleOpenChange}
      size={size}
      align={align}
      side={side}
      arrow={arrow}
    >
      <MenuTrigger
        render={(triggerProps) => (
          <Button variant={triggerVariant} size={triggerSize} {...triggerProps}>
            {triggerIcon ? (
              <Icon name={triggerIcon as IconName} size="sm" />
            ) : null}
            {triggerLabel}
          </Button>
        )}
      />

      <MenuContent>
        {items.map((item) => (
          <MenuItem
            key={item.value}
            disabled={item.disabled}
            onClick={() => onSelect?.(item.value, item)}
          >
            {item.icon ? <Icon name={item.icon as IconName} size="sm" /> : null}
            {item.label}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

componentManager.register(
  RWCMenuTrigger,
  RWCMenuContent,
  RWCMenuItem,
  rwc(MENU_TAG_NAME, {
    component: RWCMenuComponent,
    dataSchema: menuDataSchema,
    usesSlots: true,
    readSlotted: readMenuSlotted,
  }),
);
